import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { BaseURL } from "../../config/apiUrl";
import { Get } from "../../Axios/AxiosFunctions";
import "./DocPreview.css";

// The worker ships with the site, so previews do not depend on another server.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
// Fonts and character maps for PDFs that do not carry their own (symbols, bullets, Asian text).
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

/**
 * Deal room preview (redesign part 4). The server checks the viewer may see
 * the file and sends Word, Excel and PowerPoint files as PDF, so one viewer
 * draws every document the same way in every browser, phones included.
 * Buyers see their name and the date across each page.
 */
export default function DocPreview({ file, token, onClose, onDownload }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pages, setPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(800);
  const body = useRef(null);

  // Keyed on the file id and name, not the object: a new object with the same
  // file must not fetch a new link (that would swap the document mid-render).
  const fileId = file?.fileId;
  const fileName = file?.fileName || file?.name || "";
  useEffect(() => {
    let live = true;
    Get(BaseURL(`data-room/preview-link/${encodeURIComponent(fileId)}?name=${encodeURIComponent(fileName)}`), token, null, false).then((r) => {
      if (!live) return;
      const d = r?.data?.data;
      if (!d) return setError("This file could not be opened. You may not have access to it.");
      setPages(0);
      setInfo({ ...d, url: d.path ? BaseURL(d.path) : null });
    });
    return () => {
      live = false;
    };
  }, [fileId, fileName, token]);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const fit = () => body.current && setWidth(Math.min(body.current.clientWidth - 32, 1000));
    fit();
    window.addEventListener("resize", fit);
    const esc = (e) => e.key === "Escape" && closeRef.current();
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("keydown", esc);
    };
  }, []);

  const mark = info?.watermark ? `Prepared for ${info.watermark.name}, ${info.watermark.date} · Confidential · ` : "";
  const Watermark = () =>
    mark ? (
      <div className="dp-wm" aria-hidden="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} style={{ top: `${i * 11}%` }}>
            {mark.repeat(4)}
          </span>
        ))}
      </div>
    ) : null;

  return (
    <div className="dp-back" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dp">
        <div className="dp-bar">
          <b className="dp-name">{file?.fileName || file?.name}</b>
          {info?.kind === "pdf" && pages > 0 && <span className="dp-muted">{pages} {pages === 1 ? "page" : "pages"}</span>}
          <span className="dp-sp" />
          {info?.kind === "pdf" && (
            <>
              <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} aria-label="Zoom out">
                −
              </button>
              <span className="dp-muted">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((z) => Math.min(3, z + 0.25))} aria-label="Zoom in">
                +
              </button>
            </>
          )}
          {onDownload && (
            <button type="button" onClick={onDownload}>
              Download
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <div className="dp-body" ref={body}>
          {error && <div className="dp-msg">{error}</div>}
          {!error && !info && <div className="dp-msg">Opening…</div>}
          {info?.kind === "none" && <div className="dp-msg">This kind of file cannot be shown here. Download it to open it.</div>}
          {info?.kind === "image" && (
            <div className="dp-page">
              <img src={info.url} alt={file?.fileName || ""} style={{ maxWidth: "100%" }} />
              <Watermark />
            </div>
          )}
          {info?.kind === "video" && <video src={info.url} controls style={{ maxWidth: "100%" }} />}
          {info?.kind === "pdf" && (
            <Document
              key={info.url}
              file={info.url}
              options={PDF_OPTIONS}
              onLoadSuccess={({ numPages }) => setPages(numPages)}
              onLoadError={() => setError("This file could not be shown. Download it instead.")}
              loading={<div className="dp-msg">Preparing the preview. Word, Excel and PowerPoint files take a few seconds.</div>}
            >
              {Array.from({ length: Math.min(pages, 150) }).map((_, i) => (
                <div className="dp-page" key={i}>
                  <Page pageNumber={i + 1} width={width * zoom} renderTextLayer={false} renderAnnotationLayer={false} />
                  <Watermark />
                </div>
              ))}
              {pages > 150 && <div className="dp-msg">Showing the first 150 pages. Download the file to see the rest.</div>}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}
